import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Chart from "../../../Components/common/Chart/Chart";
import CustomBox from "../../../Components/UI/CustomBox/CustomBox/CustomBox";
import CustomGridContainer from "../../../Components/UI/CustomBox/CustomGrid/CustomGridContainer";
import CustomGridItem from "../../../Components/UI/CustomBox/CustomGrid/CustomGridItem";
import ThreatSummary from "./components/ThreatSummary/ThreatSummary";
import EnrichedDataVisualization from "./components/EnrichedDataVisualization/EnrichedDataVisualization";
import ThreatCategoriesChart from "./components/ThreatCategoriesChart/ThreatCategoriesChart";
import TopRiskyFiles from "./components/TopRiskyFiles/TopRiskyFiles";
import HostInformationCard from "./components/InformationCards/HostInformationCard";
import OSInformationCard from "./components/InformationCards/OSInformationCard";
import Spinner from "../../../Components/common/Spinner/Spinner";
import SecurityRatings from "./components/SecurityRatings/SecurityRatings";
import SecurityIssues from "./components/SecurityIssues/SecurityIssues";
import TopRiskyProcesses from "./components/TopRiskyProcesses/TopRiskyProcesses";
import DownloadIcon from "../../../Components/Icon-SVG/DownloadIcon";
import MLSystemIcon from "../../../Components/Icon-SVG/MLSystemIcon";
import PdfIcon from "../../../Components/Icon-SVG/PdfIcon";
import Backdrop from "../../../Components/UI/Backdrop/Backdrop";
import DownloadMessage from "../../../Components/common/DownloadMessage/DownloadMessage";
import useDownloadAgentScanFiles from "../../../utils/hook/useDownloadAgentScanFiles";
import RiskScoreGauge from "./components/RiskScoreGauge/RiskScoreGauge";
import ThreatDetailsBySeverity from "./components/ThreatDetailsBySeverity/ThreatDetailsBySeverity";
import AdditionalData from "./components/AdditionalData/AdditionalData";
import ReportExplorer from "./components/ReportExplorer/ReportExplorer";

import {
  RequestAgentAnalysisData,
  RequestGetAgentAIDecision,
} from "../../../store/actions/integrationsActions";
import { setModalActive, setModalParams } from "../../../store/actions/dashboardActions";

import "./styles.scss";

const getVerdict = (activityName, riskScore) => {
  let verdict = <span>None</span>;

  if (!riskScore) {
    return verdict;
  }

  if (riskScore > 110) {
    if (activityName) {
      verdict = <span style={{ color: "#fff", fontWeight: "700" }}>#{activityName}</span>;
    } else {
      verdict = <span>Malicious</span>;
    }
  } else if (riskScore > 30) {
    verdict = <span>Suspicious</span>;
  } else {
    verdict = <span>Clean</span>;
  }

  return verdict;
};

const getFileState = (riskScore) =>
  riskScore > 110 ? "malicious" : riskScore > 30 ? "suspicious" : "not suspicious";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const ThreatHunterAnalysisReports = () => {
  const dispatch = useDispatch();
  let query = useQuery();

  const agentAnalysisData = useSelector((state) => state.integrations.agentAnalysisData);
  const agentAnalysisDataLoading = useSelector(
    (state) => state.integrations.agentAnalysisDataLoading,
  );
  const agentAnalysisDataError = useSelector((state) => state.integrations.agentAnalysisDataError);
  const { downloadOptions, fileName, downloadAgentScanFilesHandler } = useDownloadAgentScanFiles(
    query.get("data"),
  );

  useEffect(() => {
    // setShowPopup(false);
    dispatch(
      RequestAgentAnalysisData({
        originalID: query.get("data"),
      }),
    );
    dispatch(RequestGetAgentAIDecision(query.get("data")));
  }, []);

  const askAiClickHandler = () => {
    const id = query.get("data");
    const hostname = agentAnalysisData?.system_info?.hostname || "this machine";

    // ThreatHunter questions are about a scanned *machine* (endpoint agent report),
    // so the suggested questions differ from the sandbox file analysis ones. Each
    // question ends with a [[<id>]] marker so the backend can tell it came from the
    // ThreatHunter page and populate it from the agent scan report before asking the LLM.
    const customPromptSuggestions = [
      "Is the machine " +
        hostname +
        " infected? List and explain the detected malicious activities and threats on this machine. [[" +
        id +
        "]]",
      "What are the most risky files and processes found on " +
        hostname +
        " ? Explain them. [[" +
        id +
        "]]",
      "Can you see any suspicious network activity or connections on " +
        hostname +
        " ? and explain them if there is ? [[" +
        id +
        "]]",
      "What is the security posture and system information of " + hostname + " ? [[" + id + "]]",
    ];

    // First set the parameters, then activate the modal
    dispatch(
      setModalParams({
        promptSuggestions: customPromptSuggestions,
        opensession: true,
      }),
    );

    // Now open the modal
    dispatch(setModalActive(true));
  };

  return (
    <div className="thunter-analysis-reports">
      <CustomBox style={{ padding: "18px 30px 20px 40px" }}>
        {/* <h1 className="thunter-analysis-reports-heading">Malicious Activity Detection Dashboard</h1>

        <p className="thunter-analysis-reports-subtext">
          Analysis results for potential malicious activity on the system.
        </p> */}

        <div className="analysis-reports__header">
          <div className="analysis-reports__header-doughnutChart">
            {!agentAnalysisDataLoading && agentAnalysisData && (
              <Chart
                name={`doughnut-dark`}
                type="doughnut"
                source="threat-hunter"
                // fileState={getFileState(agentAnalysisData?.risk_score || 0)}
                // decision_score={
                //   getFileState(agentAnalysisData?.risk_score || 0) === "suspicious"
                //     ? agentAnalysisData?.risk_score > 70
                //       ? 69
                //       : agentAnalysisData?.risk_score.toFixed(1)
                //     : agentAnalysisData && "confidence" in agentAnalysisData
                //       ? agentAnalysisData["confidence"] > 99
                //         ? 99
                //         : agentAnalysisData["confidence"]
                //       : 0
                // }
                fileState={
                  agentAnalysisData?.risk_score > 110
                    ? "malicious"
                    : agentAnalysisData?.risk_score > 30
                      ? "suspicious"
                      : "not suspicious"
                }
                decision_score={agentAnalysisData?.risk_score?.toFixed(1)}
              />
            )}
          </div>
          <div className="analysis-reports__header-inner" style={{ marginLeft: "10px" }}>
            <h1>
              <button
                type="button"
                className="analysis-reports__header-action"
                onClick={askAiClickHandler}
                title="Ask AI about this machine">
                <MLSystemIcon color="white" stroke="white" width={22} height={22} />
              </button>
              <Link
                className="analysis-reports__header-action"
                title="Export report"
                to={{
                  pathname: "/threat-hunter-report-export",
                  search: `?data=${query.get("data")}`,
                }}>
                <PdfIcon />
              </Link>
              <button
                type="button"
                className="analysis-reports__header-action"
                onClick={downloadAgentScanFilesHandler}
                title="Download scan outputs">
                <DownloadIcon color="white" stroke="white" width={20} height={20} />
              </button>
              <b>|</b>
              <b>
                {!agentAnalysisDataLoading &&
                agentAnalysisData &&
                "report_time" in agentAnalysisData &&
                "$date" in agentAnalysisData["report_time"]
                  ? `${agentAnalysisData["report_time"]["$date"].split("T")[0]} ${
                      agentAnalysisData["report_time"]["$date"].split("T")[1].split(".")[0]
                    }`
                  : "None"}
              </b>
              <span
                className={
                  !agentAnalysisDataLoading &&
                  agentAnalysisData &&
                  "risk_score" in agentAnalysisData
                    ? agentAnalysisData["risk_score"] > 110
                      ? "analysis-reports__header-verdict analysis-reports__header-verdict--malicious"
                      : agentAnalysisData["risk_score"] > 30
                        ? "analysis-reports__header-verdict analysis-reports__header-verdict--suspicious"
                        : "analysis-reports__header-verdict analysis-reports__header-verdict--not-suspicious"
                    : ""
                }>
                {agentAnalysisDataLoading ? (
                  <Spinner width={14} height={14} />
                ) : agentAnalysisData && "risk_score" in agentAnalysisData ? (
                  getVerdict(agentAnalysisData["activity_name"], agentAnalysisData["risk_score"])
                ) : (
                  <span>None</span>
                )}
              </span>
            </h1>
            {/* <p className="analysis-reports__header-explanation">
            You can generate pdf report by clicking the pdf icon.
          </p> */}
          </div>
          {/* <div style={{ flex: 1, marginLeft: "20px" }}>
          <ClamCloudMl setStaticResultsActive={setStaticResultsActive} />
          </div> */}
          <div style={{ flex: 1, marginLeft: "20px", display: "flex", justifyContent: "end" }}>
            <SecurityRatings ratings={agentAnalysisData?.system_info?.security_ratings || {}} />
          </div>
        </div>

        <CustomGridContainer
          justify="space-between"
          alignItems="stretch"
          style={{ marginBottom: "1rem" }}>
          {/* <CustomGridItem col={{ md: 3 }}>
            <VerdictCard
              verdict={agentAnalysisData.verdict || ""}
              activityName={agentAnalysisData.activity_name || ""}
              confidence={agentAnalysisData.confidence}
              description={agentAnalysisData.verdict_description || ""}
            />
          </CustomGridItem>

          <CustomGridItem col={{ md: 3 }}>
            <RiskScoreGauge riskScore={agentAnalysisData.risk_score} />
          </CustomGridItem> */}

          <CustomGridItem className="border" col={{ md: 3 }}>
            <HostInformationCard
              clientID={agentAnalysisData?.clientID || ""}
              hostData={agentAnalysisData?.raw_report?.systeminfo || {}}
            />
          </CustomGridItem>

          <CustomGridItem className="border" col={{ md: 3 }}>
            <OSInformationCard osData={agentAnalysisData?.raw_report?.systeminfo?.osstatus || {}} />
          </CustomGridItem>

          <CustomGridItem col={{ md: 2 }}>
            <RiskScoreGauge riskScore={agentAnalysisData?.risk_score} />
          </CustomGridItem>

          <CustomGridItem className="border" col={{ md: 4 }}>
            <ThreatSummary threatCounts={agentAnalysisData?.threat_counts} />
          </CustomGridItem>

          {/* <CustomGridItem col={{ md: 1 }}></CustomGridItem> */}
        </CustomGridContainer>
        <CustomGridContainer
          justify="space-between"
          alignItems="stretch"
          style={{ marginBottom: "1rem" }}>
          <CustomGridItem className="border" col={{ md: 4 }}>
            <ThreatCategoriesChart threatCategories={agentAnalysisData?.top_threat_categories} />
          </CustomGridItem>
          <CustomGridItem className="border" col={{ md: 5 }}>
            <TopRiskyFiles files={agentAnalysisData?.top_risky_files} />
          </CustomGridItem>
          <CustomGridItem className="border" col={{ md: 3 }}>
            <EnrichedDataVisualization
              analysisDetails={agentAnalysisData?.analysis_details}
              enrichedMaliciousHashes={agentAnalysisData?.enriched_malicious_hashes}
            />
          </CustomGridItem>
        </CustomGridContainer>
        <CustomGridContainer
          justify="space-between"
          alignItems="stretch"
          style={{ marginBottom: "3.688rem" }}>
          <CustomGridItem className="border" col={{ md: 5 }}>
            <TopRiskyProcesses processes={agentAnalysisData?.top_risky_memory_processes || []} />
          </CustomGridItem>

          <CustomGridItem className="border" col={{ md: 7 }}>
            <SecurityIssues issues={agentAnalysisData?.security_issues} />
          </CustomGridItem>
        </CustomGridContainer>
        <CustomGridContainer justify="space-between" alignItems="stretch">
          <CustomGridItem
            col={{ md: 12 }}
            style={{ backgroundColor: "transparent", marginBottom: "3.688rem" }}>
            <ReportExplorer id={query.get("data")} report={agentAnalysisData} />
          </CustomGridItem>
        </CustomGridContainer>
        <CustomGridContainer justify="space-between" alignItems="stretch">
          <CustomGridItem
            col={{ md: 12 }}
            style={{ backgroundColor: "transparent", marginBottom: "3.688rem" }}>
            <ThreatDetailsBySeverity data={agentAnalysisData?.threat_details_by_severity || {}} />
          </CustomGridItem>
        </CustomGridContainer>
        {/* <CustomGridContainer justify="space-between" alignItems="stretch">
          <CustomGridItem col={{ md: 12 }}>
            <AdditionalDataTable detectionDetails={agentAnalysisData?.detection_details || {}} />
          </CustomGridItem>
        </CustomGridContainer> */}

        <CustomGridContainer justify="space-between" alignItems="stretch">
          <CustomGridItem col={{ md: 12 }}>
            <AdditionalData id={query.get("data")} />
          </CustomGridItem>
        </CustomGridContainer>
      </CustomBox>

      <Backdrop
        show={downloadOptions.downloadStarted}
        message={
          <DownloadMessage
            fileName={fileName}
            loaded={downloadOptions.loaded}
            total={downloadOptions.total}
            progress={downloadOptions.progress}
          />
        }
      />
    </div>
  );
};

export default ThreatHunterAnalysisReports;
